/** Preset selector — dropdown to load predefined synth configurations.
 *
 * Groups presets by category (Bass, Pad, Lead, Keys) and applies
 * the selected preset's layers and delay settings to the active synth.
 */

import { BookOpen, Dices, Download, Upload } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import type { SynthPreset } from "../lib/presets.ts";
import { BUILT_IN_PRESETS } from "../lib/presets.ts";

/** Props for the PresetSelector component. */
export type PresetSelectorProps = {
	readonly onLoadPreset: (preset: SynthPreset) => void;
	readonly onRandomize?: () => void;
	readonly onExportPreset?: () => void;
	readonly onImportPreset?: (file: File) => void;
};

/** Category display labels. */
const CATEGORY_LABELS: Record<SynthPreset["category"], string> = {
	bass: "Bass",
	pad: "Pad",
	lead: "Lead",
	keys: "Keys",
	custom: "Custom",
} as const;

/** Ordered list of categories to display. */
const CATEGORY_ORDER: readonly SynthPreset["category"][] = [
	"keys",
	"bass",
	"pad",
	"lead",
] as const;

export function PresetSelector({
	onLoadPreset,
	onRandomize,
	onExportPreset,
	onImportPreset,
}: PresetSelectorProps) {
	const [isOpen, setIsOpen] = useState(false);
	const importInputRef = useRef<HTMLInputElement>(null);

	const handleSelect = useCallback(
		(preset: SynthPreset) => {
			onLoadPreset(preset);
			setIsOpen(false);
		},
		[onLoadPreset],
	);

	const handleImportFile = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (file && onImportPreset) {
				onImportPreset(file);
			}
			// Reset input so the same file can be re-imported
			e.target.value = "";
		},
		[onImportPreset],
	);

	return (
		<div className="preset-selector">
			<button
				type="button"
				className="preset-toggle-btn"
				onClick={() => setIsOpen((o) => !o)}
				title="Load synth preset"
			>
				<BookOpen size={12} />
				<span>Presets</span>
			</button>

			{onImportPreset && (
				<input
					ref={importInputRef}
					type="file"
					accept=".json"
					className="preset-import-input"
					onChange={handleImportFile}
					style={{ display: "none" }}
				/>
			)}

			{isOpen && (
				<div className="preset-dropdown">
					{/* Action buttons (randomize, export, import) */}
					<div className="preset-actions">
						{onRandomize && (
							<button
								type="button"
								className="preset-action-btn"
								onClick={() => {
									onRandomize();
									setIsOpen(false);
								}}
								title="Randomize synth parameters"
							>
								<Dices size={12} />
								<span>Randomize</span>
							</button>
						)}
						{onExportPreset && (
							<button
								type="button"
								className="preset-action-btn"
								onClick={() => {
									onExportPreset();
									setIsOpen(false);
								}}
								title="Export current preset as JSON"
							>
								<Download size={12} />
								<span>Export</span>
							</button>
						)}
						{onImportPreset && (
							<button
								type="button"
								className="preset-action-btn"
								onClick={() => {
									importInputRef.current?.click();
									setIsOpen(false);
								}}
								title="Import preset from JSON file"
							>
								<Upload size={12} />
								<span>Import</span>
							</button>
						)}
					</div>

					{CATEGORY_ORDER.map((category) => {
						const presets = BUILT_IN_PRESETS.filter(
							(p) => p.category === category,
						);
						if (presets.length === 0) return null;
						return (
							<div key={category} className="preset-category">
								<div className="preset-category-label">
									{CATEGORY_LABELS[category]}
								</div>
								{presets.map((preset) => (
									<button
										key={preset.id}
										type="button"
										className="preset-item"
										onClick={() => handleSelect(preset)}
										title={preset.description}
									>
										<span className="preset-item-name">{preset.name}</span>
										<span className="preset-item-desc">
											{preset.description}
										</span>
									</button>
								))}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}
